import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, RefreshCw, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
  const { user, organization } = useAuth();
  const [wa, setWa] = useState({ business_account_id: "", phone_number_id: "", access_token: "", verify_token: "", app_secret: "", connected: false, has_access_token: false, has_app_secret: false });
  const [verifying, setVerifying] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [cfForm, setCfForm] = useState({ name: "", key: "", type: "text", options: "" });

  const [webhookEvents, setWebhookEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const loadWebhookEvents = async () => {
    setEventsLoading(true);
    try {
      const { data } = await api.get("/whatsapp/webhook-events?limit=15");
      setWebhookEvents(data);
    } catch (e) {
      console.warn("Could not load webhook events:", e?.message);
    } finally {
      setEventsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/whatsapp/settings");
      setWa((prev) => ({ ...prev, ...data }));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      if (e?.response?.status === 400) {
        toast.error(detail || "Set up your organization first via /onboarding");
      } else {
        toast.error(`Could not load WhatsApp settings: ${detail || "unknown error"}`);
      }
    }
  };

  const loadCustomFields = async () => {
    try {
      const { data } = await api.get("/custom-fields");
      setCustomFields(data);
    } catch (e) {
      // Non-fatal: just leave the list empty and log
      console.warn("Could not load custom fields:", e?.response?.data?.detail || e?.message);
    }
  };

  useEffect(() => {
    loadSettings();
    loadCustomFields();
    loadWebhookEvents();
    // eslint-disable-next-line
  }, []);

  const saveWA = async () => {
    try {
      // Only send fields the backend expects - strip computed flags that
      // the GET endpoint adds (access_token_masked, has_access_token, etc).
      const payload = {
        business_account_id: wa.business_account_id || "",
        phone_number_id: wa.phone_number_id || "",
        access_token: wa.access_token || "",
        verify_token: wa.verify_token || "",
        app_secret: wa.app_secret || "",
      };
      await api.post("/whatsapp/settings", payload);
      toast.success("Credentials saved");
      // Refresh masked view + clear typed secrets from local state
      setWa((prev) => ({ ...prev, access_token: "", app_secret: "" }));
      await loadSettings();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      console.error("Save WhatsApp settings failed:", status, detail, e);
      toast.error(`Save failed (HTTP ${status || "?"}): ${detail}`);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/whatsapp/verify");
      if (data.connected) {
        toast.success("Connected to Meta WhatsApp API");
        setWa((prev) => ({ ...prev, connected: true }));
      } else {
        const errMsg =
          data?.error?.error?.message ||
          (typeof data?.error === "string" ? data.error : null) ||
          "Verification failed. Check credentials.";
        toast.error(errMsg);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Network error";
      toast.error(`Verify failed: ${detail}`);
    } finally {
      setVerifying(false);
    }
  };

  const addField = async () => {
    if (!cfForm.name || !cfForm.key) return toast.error("Name and key required");
    try {
      const payload = { ...cfForm, options: cfForm.options ? cfForm.options.split(",").map((s) => s.trim()) : [] };
      await api.post("/custom-fields", payload);
      setCfForm({ name: "", key: "", type: "text", options: "" });
      await loadCustomFields();
      toast.success("Field added");
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e?.message}`);
    }
  };

  const delField = async (id) => {
    try {
      await api.delete(`/custom-fields/${id}`);
      setCustomFields((prev) => prev.filter((f) => f.id !== id));
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e?.message}`);
    }
  };

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="settings-page">
      <PageHeader title="Settings" description="Manage your workspace, WhatsApp credentials and custom fields" />

      <Tabs defaultValue="whatsapp">
        <TabsList>
          <TabsTrigger value="whatsapp" data-testid="settings-tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="organization" data-testid="settings-tab-organization">Organization</TabsTrigger>
          <TabsTrigger value="custom-fields" data-testid="settings-tab-custom-fields">Custom Fields</TabsTrigger>
        </TabsList>

        <TabsContent value="whatsapp" className="mt-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                {wa.connected ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <div className="font-medium text-slate-900">Connected to Meta WhatsApp API</div>
                    <span className="ml-2 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700" data-testid="wa-status-connected">
                      Connected
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    <div className="font-medium text-slate-900">Running in stub mode</div>
                    <span className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700" data-testid="wa-status-stub">
                      Stub mode — no credentials
                    </span>
                  </>
                )}
              </div>
              <p className="text-sm text-slate-500 mb-5">
                Configure your Meta WhatsApp Business credentials. While unconfigured, messages run in stub mode (logged only).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "business_account_id", label: "Business Account ID" },
                  { key: "phone_number_id", label: "Phone Number ID" },
                  { key: "access_token", label: "Permanent Access Token", type: "password", sensitive: true, has_key: "has_access_token" },
                  { key: "verify_token", label: "Webhook Verify Token" },
                  { key: "app_secret", label: "App Secret (HMAC)", type: "password", sensitive: true, has_key: "has_app_secret" },
                ].map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs font-semibold tracking-wider uppercase text-slate-600">{f.label}</Label>
                    <Input
                      type={f.type || "text"}
                      value={wa[f.key] || ""}
                      onChange={(e) => setWa({ ...wa, [f.key]: e.target.value })}
                      className="mt-1.5"
                      placeholder={f.sensitive && wa[f.has_key] ? "•••••••• stored (leave blank to keep)" : ""}
                      data-testid={`settings-wa-${f.key}`}
                    />
                    {f.key === "app_secret" && (
                      <p className="text-xs text-slate-500 mt-1">
                        Used to verify <code>X-Hub-Signature-256</code> on incoming webhooks. Find it in Meta App Dashboard → Settings → Basic → App Secret.
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {wa.has_app_secret ? (
                <div className="mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="webhook-hmac-enabled">
                  🔒 Webhook HMAC signature verification is <strong>active</strong>. Only requests signed by your Meta App will be accepted.
                </div>
              ) : (
                <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="webhook-hmac-disabled">
                  ⚠ Webhook HMAC verification is <strong>disabled</strong>. Add your App Secret above to require signed webhooks.
                </div>
              )}

              <div className="mt-5 p-4 rounded-md bg-slate-50 border border-slate-200">
                <div className="label-caps mb-1">Webhook URL</div>
                <code className="text-xs text-slate-700 break-all font-mono">{webhookUrl}</code>
                <p className="text-xs text-slate-500 mt-2">
                  Set this URL in Meta App Dashboard → WhatsApp → Configuration → Webhook, with your verify token.
                </p>
              </div>

              {/* Webhook Activity / Debug Panel */}
              <div className="mt-5 border border-slate-200 rounded-md" data-testid="webhook-activity-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                  <div>
                    <div className="label-caps">Webhook activity</div>
                    <div className="text-xs text-slate-500 mt-0.5">Live trace of POSTs Meta sends to your webhook URL</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={loadWebhookEvents} disabled={eventsLoading} data-testid="webhook-activity-refresh">
                    {eventsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                {webhookEvents.length === 0 ? (
                  <div className="p-6 text-center text-xs text-slate-500" data-testid="webhook-activity-empty">
                    No webhook events received yet. If customers are replying but nothing shows up here:
                    <ol className="list-decimal mt-2 text-left max-w-md mx-auto space-y-1 ml-6">
                      <li>Verify the Webhook URL above is pasted EXACTLY into Meta App → WhatsApp → Configuration.</li>
                      <li>Verify the Verify token in Meta matches the one shown in the form above.</li>
                      <li>In Meta App → WhatsApp → Configuration → Webhook fields, click <strong>Manage</strong> and subscribe to <code>messages</code>.</li>
                      <li>Use the <strong>Test</strong> button in Meta's webhook UI to send a sample event — it should appear here within 5 seconds.</li>
                    </ol>
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                    {webhookEvents.map((e) => {
                      const ok = e.outcome === "processed";
                      const tone = ok
                        ? "text-emerald-700"
                        : ["signature_missing","signature_malformed","signature_mismatch"].includes(e.outcome)
                        ? "text-red-700"
                        : "text-amber-700";
                      return (
                        <li key={e.id} className="px-4 py-2.5 text-xs" data-testid={`webhook-event-${e.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className={`font-medium font-mono ${tone}`}>
                                {ok ? "✓" : "⚠"} {e.outcome} {e.status_code !== 200 ? `· HTTP ${e.status_code}` : ""}
                              </div>
                              <div className="text-slate-500 mt-0.5">
                                {new Date(e.received_at).toLocaleString()} · from {e.source_ip}
                              </div>
                              <div className="text-slate-600 mt-0.5">
                                phone_number_id: <code className="font-mono">{e.phone_number_id || "—"}</code>
                                {ok && ` · ${e.messages_processed} msgs, ${e.statuses_processed} statuses`}
                              </div>
                              {e.error && <div className="text-red-700 mt-0.5 font-mono">{e.error}</div>}
                            </div>
                            <div className="text-[10px] text-slate-400 whitespace-nowrap">
                              {e.signature_present ? "🔒 signed" : "🔓 unsigned"}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={verify} disabled={verifying} data-testid="settings-wa-verify">
                  {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" />Verify</>}
                </Button>
                <Button onClick={saveWA} className="bg-brand hover:bg-brand-dark" data-testid="settings-wa-save">
                  Save credentials
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization" className="mt-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6 space-y-4">
              <div>
                <Label className="text-xs">Organization name</Label>
                <Input value={organization?.name || ""} disabled className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Owner</Label>
                <Input value={user?.email || ""} disabled className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Industry</Label>
                <Input value={organization?.industry || "—"} disabled className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Timezone</Label>
                <Input value={organization?.timezone || ""} disabled className="mt-1" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custom-fields" className="mt-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
                <Input placeholder="Field name" value={cfForm.name} onChange={(e) => setCfForm({ ...cfForm, name: e.target.value })} data-testid="cf-name" />
                <Input placeholder="key (no spaces)" value={cfForm.key} onChange={(e) => setCfForm({ ...cfForm, key: e.target.value })} data-testid="cf-key" />
                <Select value={cfForm.type} onValueChange={(v) => setCfForm({ ...cfForm, type: v })}>
                  <SelectTrigger data-testid="cf-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["text", "number", "dropdown", "date", "checkbox", "boolean"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addField} className="bg-brand hover:bg-brand-dark" data-testid="cf-add">
                  <Plus className="w-4 h-4 mr-1.5" />Add field
                </Button>
              </div>
              {cfForm.type === "dropdown" && (
                <Input placeholder="Options (comma separated)" value={cfForm.options} onChange={(e) => setCfForm({ ...cfForm, options: e.target.value })} className="mb-4" />
              )}

              <div className="border-t border-slate-200 pt-4">
                {customFields.length === 0 ? (
                  <div className="text-sm text-slate-400 text-center py-4">No custom fields yet</div>
                ) : (
                  <ul className="space-y-2">
                    {customFields.map((f) => (
                      <li key={f.id} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2">
                        <div>
                          <div className="text-sm font-medium text-slate-800">{f.name}</div>
                          <div className="text-xs text-slate-500">key: <code className="bg-slate-100 px-1">{f.key}</code> · type: {f.type}</div>
                        </div>
                        <button onClick={() => delField(f.id)} className="text-slate-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
